import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Card, PageHeader } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { UserCog, Plus, Copy, Shield, KeyRound, Trash2 } from "lucide-react";
import { FEATURES } from "@/lib/features";

const PERM_LABELS = {
  view_leads: "View Leads", edit_leads: "Edit Leads", view_clients: "View Clients", edit_clients: "Edit Clients",
  view_cases: "View Cases", edit_cases: "Edit Cases", view_documents: "View Documents", upload_documents: "Upload Documents",
  delete_documents: "Delete Documents", view_billing: "View Billing", manage_billing: "Manage Billing",
  view_revenue: "View Revenue",
  view_reports: "View Reports", manage_appointments: "Manage Appointments", manage_tasks: "Manage Tasks",
  view_communications: "View Communications", manage_settings: "Manage Settings",
};

export default function AdminStaff() {
  const { user } = useAuth();
  const [staff, setStaff] = useState(null);
  const [perms, setPerms] = useState({ all: [], default_staff: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwStaff, setPwStaff] = useState(null);
  const [delStaff, setDelStaff] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/staff/${delStaff.id}`);
      toast.success(`${delStaff.name} removed`);
      setDelStaff(null);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setDeleting(false); }
  };

  const load = () => api.get("/staff").then((r) => setStaff(r.data)).catch(() => setStaff([]));
  useEffect(() => { load(); api.get("/permissions").then((r) => setPerms(r.data)).catch(() => {}); }, []);

  if (user?.role !== "admin") return <Card className="p-6">Only administrators can manage staff.</Card>;
  if (!staff) return <Loader />;

  return (
    <div>
      <PageHeader title="Staff & Roles" subtitle="Manage team members and their granular permissions.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><button data-testid="add-staff-btn" className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm"><Plus className="h-4 w-4" /> New Staff</button></DialogTrigger>
          <StaffDialog perms={perms} onDone={() => { setOpen(false); load(); }} />
        </Dialog>
      </PageHeader>

      <div className="grid gap-4">
        {staff.map((s) => (
          <Card key={s.id} className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-secondary text-white flex items-center justify-center font-head font-bold shrink-0">{s.name?.[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-head font-bold text-slate-900">{s.name}</span>
                  <StatusBadge status={s.role === "admin" ? "Completed" : s.active ? "Active" : "Cancelled"} />
                  {s.role === "admin" && <span className="text-[10px] font-bold text-primary flex items-center gap-1"><Shield className="h-3 w-3" /> ADMIN</span>}
                </div>
                <div className="text-sm text-slate-500">{s.username ? `@${s.username}` : s.email} · {s.title || "Staff"}</div>
                {s.role !== "admin" && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(s.permissions || []).filter((p) => FEATURES.documents || !p.endsWith("_documents")).map((p) => <span key={p} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{PERM_LABELS[p] || p}</span>)}
                    {(s.permissions || []).filter((p) => FEATURES.documents || !p.endsWith("_documents")).length === 0 && <span className="text-xs text-slate-400">No permissions</span>}
                  </div>
                )}
              </div>
              {(s.id === user.id || s.role !== "admin") && (
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`set-password-${s.id}`} onClick={() => setPwStaff(s)} className="px-4 py-2 border border-slate-200 rounded-xl text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-slate-50"><KeyRound className="h-3.5 w-3.5" /> {s.id === user.id ? "My password" : "Password"}</button>
                  {s.role !== "admin" && s.id !== user.id && (
                    <>
                      <Dialog open={editing === s.id} onOpenChange={(o) => setEditing(o ? s.id : null)}>
                        <DialogTrigger asChild><button data-testid={`edit-staff-${s.id}`} className="px-4 py-2 border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">Edit permissions</button></DialogTrigger>
                        <StaffDialog perms={perms} existing={s} onDone={() => { setEditing(null); load(); }} />
                      </Dialog>
                      <button data-testid={`delete-staff-${s.id}`} onClick={() => setDelStaff(s)} className="px-3 py-2 border border-rose-200 text-rose-600 rounded-xl text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                    </>
                  )}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {pwStaff && (
        <Dialog open={!!pwStaff} onOpenChange={(o) => !o && setPwStaff(null)}>
          <PasswordDialog staff={pwStaff} onDone={() => setPwStaff(null)} />
        </Dialog>
      )}

      {delStaff && (
        <Dialog open={!!delStaff} onOpenChange={(o) => !o && setDelStaff(null)}>
          <DialogContent className="sm:max-w-md" data-testid="delete-staff-dialog">
            <DialogHeader><DialogTitle>Delete staff member</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Are you sure you want to permanently delete <b>{delStaff.name}</b>? They will lose access immediately and be unassigned from all clients, cases, leads and tasks. This cannot be undone.</p>
              <DialogFooter className="gap-2">
                <button onClick={() => setDelStaff(null)} className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold">Cancel</button>
                <button data-testid="confirm-delete-staff" onClick={doDelete} disabled={deleting} className="px-5 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-semibold disabled:opacity-60">{deleting ? "Deleting..." : "Delete staff"}</button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function PasswordDialog({ staff, onDone }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try { await api.put(`/staff/${staff.id}`, { password: pw }); toast.success(`Password updated for ${staff.name}`); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader><DialogTitle>Set password · {staff.name}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <p className="text-sm text-slate-500">Set a new password for this account, then share it securely. The admin can change it again here at any time.</p>
        <div className="flex gap-2">
          <input data-testid="new-password-input" type="text" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-mono" />
          <button onClick={() => setPw(Math.random().toString(36).slice(2, 10) + "A1!")} className="px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold shrink-0 hover:bg-slate-50">Generate</button>
        </div>
        <DialogFooter><button data-testid="save-password-btn" onClick={save} disabled={saving} className="bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm w-full disabled:opacity-60">{saving ? "Saving..." : "Update password"}</button></DialogFooter>
      </div>
    </DialogContent>
  );
}

function StaffDialog({ perms, existing, onDone }) {
  const isEdit = !!existing;
  const [f, setF] = useState(isEdit
    ? { name: existing.name, title: existing.title || "", role: existing.role, active: existing.active, is_consultant: existing.is_consultant || false, birthday: existing.birthday || "", work_anniversary: existing.work_anniversary || "", permissions: existing.permissions || [] }
    : { name: "", username: "", title: "", password: "", role: "staff", is_consultant: false, birthday: "", work_anniversary: "", permissions: perms.default_staff });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const togglePerm = (p) => setF((s) => ({ ...s, permissions: s.permissions.includes(p) ? s.permissions.filter((x) => x !== p) : [...s.permissions, p] }));

  const submit = async () => {
    setSaving(true);
    try {
      if (isEdit) { await api.put(`/staff/${existing.id}`, f); toast.success("Staff updated"); onDone(); }
      else {
        if (!f.name || !f.username) { toast.error("Name and username required"); setSaving(false); return; }
        const { data } = await api.post("/staff", f); setResult(data); toast.success("Staff created");
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
      <DialogHeader><DialogTitle>{isEdit ? `Edit ${existing.name}` : "New Staff Member"}</DialogTitle></DialogHeader>
      {result ? (
        <div className="space-y-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-700">
            Staff created. Login username: <b>{result.username}</b><br />Temp password: <b className="font-mono">{result.temp_password}</b>
            <button onClick={() => { navigator.clipboard.writeText(result.temp_password); toast.success("Copied"); }} className="ml-2 inline-flex items-center gap-1 text-xs"><Copy className="h-3 w-3" /> copy</button>
          </div>
          <DialogFooter><button onClick={onDone} className="bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm w-full">Done</button></DialogFooter>
        </div>
      ) : (
        <div className="space-y-3">
          {!isEdit && <input data-testid="staff-name" placeholder="Full name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />}
          {isEdit && <input data-testid="staff-name" placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />}
          {!isEdit && <input data-testid="staff-username" placeholder="Username * (used to sign in)" autoCapitalize="none" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />}
          <input placeholder="Job title (e.g. Case Manager)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-medium text-slate-500">Birthday</label>
              <input data-testid="staff-birthday" type="date" value={f.birthday} onChange={(e) => setF({ ...f, birthday: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-500">Work anniversary</label>
              <input data-testid="staff-work-anniversary" type="date" value={f.work_anniversary} onChange={(e) => setF({ ...f, work_anniversary: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
            </div>
          </div>
          <p className="text-[11px] text-slate-400 -mt-1">Used for a personal greeting on this person's dashboard on the day. Leave blank to skip.</p>
          {isEdit && (
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active account</label>
          )}
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.is_consultant} onChange={(e) => setF({ ...f, is_consultant: e.target.checked })} /> Is a consultant (RCIC)</label>

          <div>
            <div className="text-sm font-semibold text-slate-700 mb-2">Permissions</div>
            {(perms.groups || [{ group: "All", perms: (perms.all || []).map((p) => [p, PERM_LABELS[p] || p]) }])
              .map((grp) => ({ ...grp, perms: grp.perms.filter(([p]) => FEATURES.documents || !p.endsWith("_documents")) }))
              .filter((grp) => grp.perms.length).map((grp) => (
              <div key={grp.group} className="mb-3">
                <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">{grp.group}</div>
                <div className="grid grid-cols-2 gap-2">
                  {grp.perms.map(([p, label]) => (
                    <label key={p} className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 rounded-lg px-2.5 py-2 cursor-pointer">
                      <input type="checkbox" data-testid={`perm-${p}`} checked={f.permissions.includes(p)} onChange={() => togglePerm(p)} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><button data-testid="staff-submit" onClick={submit} disabled={saving} className="bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm w-full disabled:opacity-60">{saving ? "Saving..." : isEdit ? "Save changes" : "Create staff"}</button></DialogFooter>
        </div>
      )}
    </DialogContent>
  );
}
